import { useCallback, useMemo, useState } from 'react';
import type { IntegrationData } from '../types';
import { useSendToIntegrationContainerData } from './useSendToIntegrationContainerData';
import { type UniversalIssue } from '@deque/axe-devtools-issues-utils';

export type SendIssueEventArgs = {
  integrationProductSlug: string;
  project: string;
  issueType: string;
  template: string;
  isMultipleShare?: boolean;
};
export type SendToIntegrationContainerProps = {
  hostProduct: { name: string };
  apiHostname: string;
  userEnterpriseId: string;
  authFn: () => Promise<string>; // returns walnut Bearer token
  issueIDExtractorFn: (issue: UniversalIssue) => string; // returns issue ID from an issue object
  issuesFn: () => Promise<UniversalIssue[]>; // returns the issue object
  subscriptionsFn?: () => Promise<string[]>; // array of product slugs that user has access to
  supportedIntegrationSlugs?: string[]; // array of product slugs that should be supported
  onSuccessSendIssue?: (args: SendIssueEventArgs) => void; // called when issue is successfully sent
  onInitiateSendIssue?: (args: SendIssueEventArgs) => void; // called when user clicks on send issue button
  onErrorSendIssue?: (args: SendIssueEventArgs) => void; // called when the error occurs on sending issue
  userId?: string; // user id to check user integration product access
};

export const useSendToIntegrationLogic = (
  sendToIntegrationProps: SendToIntegrationContainerProps
) => {
  const [selectedIntegrationData, setsSelectedIntegrationData] =
    useState<IntegrationData | null>(null);

  const { sendToIntegrationContainerData, loading, error, refetchIssues } =
    useSendToIntegrationContainerData(sendToIntegrationProps);

  const onModalClose = useCallback(() => setsSelectedIntegrationData(null), []);

  const onSendIssueSuccess = useCallback(
    (args: SendIssueEventArgs) => {
      refetchIssues();
      setsSelectedIntegrationData(null);
      sendToIntegrationProps.onSuccessSendIssue?.(args);
    },
    [refetchIssues]
  );

  const props = useMemo(
    () => ({
      apiHostname: sendToIntegrationProps.apiHostname,
      authFn: sendToIntegrationProps.authFn,
      userEnterpriseId: sendToIntegrationProps.userEnterpriseId,
      selectedIntegrationData,
      sendToIntegrationContainerData,
      onModalClose,
      onSendIssueSuccess,
      setsSelectedIntegrationData
    }),
    [
      sendToIntegrationProps.apiHostname,
      sendToIntegrationProps.authFn,
      sendToIntegrationProps.userEnterpriseId,
      selectedIntegrationData,
      sendToIntegrationContainerData,
      onModalClose,
      onSendIssueSuccess
    ]
  );

  return {
    loading,
    error,
    props
  };
};
